import { ADMIN_ROLES } from '../api/enums';
import { AdminGrade } from '../types';

/**
 * Canonical list of Nigerian states + FCT (37 values).
 * Mirrors the backend `NIGERIAN_STATES` constant used to validate
 * `user_tax_profiles.state`. Keep both lists in sync.
 */
export const NIGERIAN_STATES_LIST: string[] = [
  'Abia',
  'Adamawa',
  'Akwa Ibom',
  'Anambra',
  'Bauchi',
  'Bayelsa',
  'Benue',
  'Borno',
  'Cross River',
  'Delta',
  'Ebonyi',
  'Edo',
  'Ekiti',
  'Enugu',
  'Gombe',
  'Imo',
  'Jigawa',
  'Kaduna',
  'Kano',
  'Katsina',
  'Kebbi',
  'Kogi',
  'Kwara',
  'Lagos',
  'Nasarawa',
  'Niger',
  'Ogun',
  'Ondo',
  'Osun',
  'Oyo',
  'Plateau',
  'Rivers',
  'Sokoto',
  'Taraba',
  'Yobe',
  'Zamfara',
  'FCT',
];

/** `{ label, value }` option shape for `Select` / `MultiSelect` components. */
export const NIGERIAN_STATES: Array<{ label: string; value: string }> = NIGERIAN_STATES_LIST.map((state) => ({
  label: state,
  value: state,
}));

/** TIN format: alphanumeric + hyphen, 1–14 chars. */
export const TIN_REGEX = /^[A-Za-z0-9-]{1,14}$/;

/** Max rows accepted per bulk tax-profile upload (matches backend cap). */
export const TAX_PROFILE_BULK_MAX_ROWS = 500;

/** Mirrors the backend `USER_FLAGS` enum; any other `flagId` is rejected. Keep both lists in sync. */
export const USER_FLAGS = {
  BVN_NAME_MISMATCH: 'BVN Name No Match',
  NIN_BVN_MISMATCH: 'NIN BVN No Match',
  NIN_RELEASED_TO_OWNER: 'NIN Released To Owner',
  LARGE_VALUE_CORE_TRANSACTIONS: 'largevaluecoretransactions',
  FREEZE: 'freeze',
  SAME_DEBIT_CARD_IN_USE: 'sameDebitCardInUse',
  MULTIPLE_TARGET_WITHDRAWALS: 'doubletargetwithdrawal',
  DAILY_CREDIT_VELOCITY: 'dailycreditvelocity',
  DAILY_DEBIT_VELOCITY: 'dailydebitvelocity',
  DAILY_MANY_TO_ONE: 'dailymanytoone',
  IMMEDIATE_CREDIT_DEBIT: 'immediatecreditdebit',
  ACTIVITY_ON_NEW_ACCOUNT: 'activityonnewaccount',
};

export const USER_FLAG_OPTIONS: Array<{ label: string; value: string }> = [
  { label: 'BVN Name Mismatch', value: USER_FLAGS.BVN_NAME_MISMATCH },
  { label: 'NIN BVN Mismatch', value: USER_FLAGS.NIN_BVN_MISMATCH },
  { label: 'NIN Released To Owner', value: USER_FLAGS.NIN_RELEASED_TO_OWNER },
  { label: 'Large Value Core Transactions', value: USER_FLAGS.LARGE_VALUE_CORE_TRANSACTIONS },
  { label: 'Freeze', value: USER_FLAGS.FREEZE },
  { label: 'Same Debit Card In Use', value: USER_FLAGS.SAME_DEBIT_CARD_IN_USE },
  { label: 'Multiple Target Withdrawals', value: USER_FLAGS.MULTIPLE_TARGET_WITHDRAWALS },
  { label: 'Daily Credit Velocity', value: USER_FLAGS.DAILY_CREDIT_VELOCITY },
  { label: 'Daily Debit Velocity', value: USER_FLAGS.DAILY_DEBIT_VELOCITY },
  { label: 'Daily Many To One', value: USER_FLAGS.DAILY_MANY_TO_ONE },
  { label: 'Immediate Credit Debit', value: USER_FLAGS.IMMEDIATE_CREDIT_DEBIT },
  { label: 'Activity On New Account', value: USER_FLAGS.ACTIVITY_ON_NEW_ACCOUNT },
];

/**
 * Option sets for editing a flex-dollar (USD) KYC request.
 * Values match the slugs stored on the KYC document, so an edited record keeps
 * rendering the same way the rest of the USD request screens expect.
 */
export const USD_IDENTITY_TYPE_OPTIONS: Array<{ label: string; value: string }> = [
  { label: 'International Passport', value: 'international passport' },
  { label: "Driver's Licence", value: 'drivers_licence' },
  { label: 'National ID', value: 'national_id' },
];

export const USD_EMPLOYMENT_STATUS_OPTIONS: Array<{ label: string; value: string }> = [
  { label: 'Employed', value: 'employed' },
  { label: 'Self Employed', value: 'self_employed' },
  { label: 'Unemployed', value: 'unemployed' },
  { label: 'Student', value: 'student' },
  { label: 'Retired', value: 'retired' },
];

export const USD_PRIMARY_PURPOSE_OPTIONS: Array<{ label: string; value: string }> = [
  { label: 'Personal', value: 'personal' },
  { label: 'Business', value: 'business' },
];

export const USD_SOURCE_OF_FUNDS_OPTIONS: Array<{ label: string; value: string }> = [
  { label: 'Salary', value: 'salary' },
  { label: 'Business Income', value: 'business_income' },
  { label: 'Investments', value: 'investments' },
  { label: 'Savings', value: 'savings' },
  { label: 'Gift', value: 'gift' },
  { label: 'Inheritance', value: 'inheritance' },
  { label: 'Other', value: 'other' },
];

/** Document slots the file-replacement endpoint accepts. */
export const USD_DOCUMENT_FIELDS = {
  IDENTITY: 'identity_url_links',
  UTILITY_BILL: 'utility_bill_url',
  BANK_STATEMENT: 'bank_statement_url',
} as const;

export const GRADE_1_ROLE_IDS: string[] = [ADMIN_ROLES.intern];

export const DEFAULT_ADMIN_GRADE: AdminGrade = 2;

export const ACTION_MINIMUM_GRADE: Record<string, AdminGrade> = {
  'customerPage.editPhoneNumber': 1,
  'customerPage.editEmailAddress': 1,
  'manageDevices.deleteDevice': 2,
  'flexnaira.payInterestByDay': 2,
  'housemoney.changeEndDate': 2,
  'housemoney.fixInterest': 2,
  'target.payInterest': 2,
};

/** Actions limited to named roles rather than a grade floor. Takes precedence over the grade map. */
export const ACTION_ALLOWED_ROLE_IDS: Record<string, string[]> = {
  'withdrawalManager.withdrawForUser': [ADMIN_ROLES.super_admin, ADMIN_ROLES.csm, ADMIN_ROLES.team_leads],
};
